import React, { useState, useEffect } from 'react';
import { Target, User, MapPin, AlertCircle } from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';

/**
 * Win/Loss Analysis Component
 * Shows approval rates and rejection patterns
 */
export default function WinLossAnalysis({ dateRange }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('service'); // 'service', 'ae', 'state'

  useEffect(() => {
    fetchWinLossData();
  }, [dateRange]);

  const fetchWinLossData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      let url = `${process.env.REACT_APP_BACKEND_URL}/api/analytics/advanced/win-loss-analysis`;
      
      if (dateRange?.startDate) {
        url += `?start_date=${dateRange.startDate}`;
      }
      if (dateRange?.endDate) {
        url += `${dateRange?.startDate ? '&' : '?'}end_date=${dateRange.endDate}`;
      }
      
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const result = await response.json();
      setData(result);
    } catch (error) {
      console.error('Error fetching win/loss data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg p-6 shadow-sm border border-slate-200">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-slate-200 rounded w-1/3"></div>
          <div className="grid grid-cols-2 gap-4">
            <div className="h-64 bg-slate-200 rounded"></div>
            <div className="h-64 bg-slate-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const overallData = [
    { name: 'Approved', value: data.overall.total_approved, color: '#10b981' },
    { name: 'Rejected', value: data.overall.total_rejected, color: '#ef4444' },
  ];

  const tabs = [
    { id: 'service', label: 'By Service', icon: Target },
    { id: 'ae', label: 'By Account Executive', icon: User },
    { id: 'state', label: 'By State', icon: MapPin },
  ];

  const getTabData = () => {
    switch (activeTab) {
      case 'service':
        return data.by_service || [];
      case 'ae':
        return data.by_account_executive || [];
      case 'state':
        return data.by_state || [];
      default:
        return [];
    }
  };

  const getLabel = (item) => {
    switch (activeTab) {
      case 'service':
        return item.service;
      case 'ae':
        return item.ae_name;
      case 'state':
        return item.state;
      default:
        return '';
    }
  };

  const tabData = getTabData();

  return (
    <div className="bg-white rounded-lg p-6 shadow-sm border border-slate-200">
      {/* Header */}
      <div className="flex items-center gap-2 mb-6">
        <Target className="w-5 h-5 text-blue-600" />
        <h3 className="text-lg font-bold text-slate-900">Win/Loss Analysis</h3>
      </div>

      {/* Overall Stats */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <div className="bg-blue-50 rounded-lg p-4">
          <p className="text-xs text-blue-600 uppercase tracking-wide mb-1">Total Submitted</p>
          <p className="text-2xl font-bold text-blue-900">{data.overall.total_submitted}</p>
        </div>
        <div className="bg-green-50 rounded-lg p-4">
          <p className="text-xs text-green-600 uppercase tracking-wide mb-1">Approved</p>
          <p className="text-2xl font-bold text-green-900">{data.overall.total_approved}</p>
        </div>
        <div className="bg-red-50 rounded-lg p-4">
          <p className="text-xs text-red-600 uppercase tracking-wide mb-1">Rejected</p>
          <p className="text-2xl font-bold text-red-900">{data.overall.total_rejected}</p>
        </div>
        <div className="bg-purple-50 rounded-lg p-4">
          <p className="text-xs text-purple-600 uppercase tracking-wide mb-1">Approval Rate</p>
          <p className="text-2xl font-bold text-purple-900">
            {data.overall.overall_approval_rate.toFixed(1)}%
          </p>
        </div>
      </div>

      {/* Pie Chart & Rejection Reasons */}
      <div className="grid grid-cols-2 gap-6 mb-6">
        {/* Overall Pie Chart */}
        <div>
          <h4 className="text-sm font-semibold text-slate-700 mb-3">Overall Distribution</h4>
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie
                data={overallData}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={80}
                paddingAngle={5}
                dataKey="value"
              >
                {overallData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Rejection Reasons */}
        <div>
          <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
            <AlertCircle className="w-4 h-4 text-red-600" />
            Top Rejection Reasons
          </h4>
          <div className="space-y-2">
            {data.rejection_reasons.slice(0, 5).map((reason, index) => (
              <div key={index} className="flex items-center justify-between bg-red-50 rounded-lg p-3">
                <p className="text-sm text-slate-700 flex-1">{reason.reason}</p>
                <span className="text-sm font-bold text-red-700 ml-2">{reason.count}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-4 border-b border-slate-200">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 font-medium text-sm transition-colors border-b-2 ${
              activeTab === tab.id
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-slate-600 hover:text-slate-900'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Data Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200">
              <th className="text-left py-2 px-3 font-semibold text-slate-600">
                {activeTab === 'service' ? 'Service' : activeTab === 'ae' ? 'Account Executive' : 'State'}
              </th>
              <th className="text-right py-2 px-3 font-semibold text-slate-600">Approved</th>
              <th className="text-right py-2 px-3 font-semibold text-slate-600">Rejected</th>
              <th className="text-right py-2 px-3 font-semibold text-slate-600">Pending</th>
              <th className="text-right py-2 px-3 font-semibold text-slate-600">Approval Rate</th>
            </tr>
          </thead>
          <tbody>
            {tabData.map((item, index) => (
              <tr key={index} className="border-b border-slate-100 hover:bg-slate-50">
                <td className="py-2 px-3 font-medium text-slate-900">{getLabel(item)}</td>
                <td className="py-2 px-3 text-right text-green-700 font-medium">{item.approved}</td>
                <td className="py-2 px-3 text-right text-red-700 font-medium">{item.rejected}</td>
                <td className="py-2 px-3 text-right text-slate-600">{item.pending}</td>
                <td className="py-2 px-3 text-right">
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                    item.approval_rate >= 80 ? 'bg-green-100 text-green-700' :
                    item.approval_rate >= 60 ? 'bg-yellow-100 text-yellow-700' :
                    'bg-red-100 text-red-700'
                  }`}>
                    {item.approval_rate.toFixed(1)}%
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
